import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';

// Layouts
import MainLayout from './components/layout/MainLayout';
import PortalLayout from './components/layout/PortalLayout';

// Providers
import { ToastProvider, TooltipProvider } from './components/ui';

// Pages
import LoginPage from './pages/Login';
import OAuthCallbackPage from './pages/OAuthCallback';
import Dashboard from './pages/dashboard';
import { ClientesPage, ClienteDetallePage } from './pages/clientes';
import { ConversacionesPage, ConversacionChatPage } from './pages/conversaciones';
import { TicketsPage, TicketDetallePage } from './pages/tickets';
import { ActividadesPage, ActividadDetallePage, ImportarActividadesPage } from './pages/actividades';
import { ConocimientoPage, ArticuloDetallePage } from './pages/conocimiento';
import { ProyectosPage, ProyectoDetallePage } from './pages/proyectos';
import { ReportesPage } from './pages/reportes';
import { DashboardsPage, DashboardViewPage, DashboardEditPage } from './pages/dashboards';
import { KanbanPage, TableroKanbanPage } from './pages/kanban';
import {
  PortalDashboard,
  PortalTicketsPage,
  PortalTicketDetallePage,
  PortalNuevoTicketPage,
  PortalConversacionesPage,
  PortalConversacionPage,
  PortalNuevaConversacionPage,
  PortalConocimientoPage,
  PortalArticuloPage,
  PortalPerfilPage,
} from './pages/portal';
import { MasPage } from './pages/mobile/MasPage';
import {
  ConfiguracionPage,
  PerfilPage,
  PreferenciasPage,
  UsuariosPage,
  RolesPage,
  SistemaPage,
  AutomatizacionesPage,
} from './pages/configuracion';
import { EncuestaPage } from './pages/csat';
import { CmdbPage } from './pages/cmdb';

// Componente para rutas protegidas
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

function App() {
  return (
    <TooltipProvider>
    <Routes>
      {/* Ruta de login */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
      
      {/* Rutas protegidas */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/actividades" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="conversaciones" element={<ConversacionesPage />} />
        <Route path="conversaciones/:id" element={<ConversacionChatPage />} />
        <Route path="tickets" element={<TicketsPage />} />
        <Route path="tickets/:id" element={<TicketDetallePage />} />
        <Route path="actividades" element={<ActividadesPage />} />
        <Route path="actividades/importar" element={<ImportarActividadesPage />} />
        <Route path="actividades/:id" element={<ActividadDetallePage />} />
        <Route path="proyectos" element={<ProyectosPage />} />
        <Route path="proyectos/:id" element={<ProyectoDetallePage />} />
        <Route path="conocimiento" element={<ConocimientoPage />} />
        <Route path="conocimiento/:id" element={<ArticuloDetallePage />} />
        <Route path="clientes" element={<ClientesPage />} />
        <Route path="clientes/:id" element={<ClienteDetallePage />} />
        <Route path="reportes" element={<ReportesPage />} />
        <Route path="dashboards" element={<DashboardsPage />} />
        <Route path="dashboards/:id" element={<DashboardViewPage />} />
        <Route path="dashboards/:id/editar" element={<DashboardEditPage />} />
        <Route path="kanban" element={<KanbanPage />} />
        <Route path="kanban/:id" element={<TableroKanbanPage />} />
        <Route path="cmdb" element={<CmdbPage />} />
        <Route path="mas" element={<MasPage />} />
        <Route path="configuracion" element={<ConfiguracionPage />}>
          <Route path="perfil" element={<PerfilPage />} />
          <Route path="preferencias" element={<PreferenciasPage />} />
          <Route path="usuarios" element={<UsuariosPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="sistema" element={<SistemaPage />} />
          <Route path="automatizaciones" element={<AutomatizacionesPage />} />
        </Route>
      </Route>

      {/* Portal Cliente */}
      <Route
        path="/portal"
        element={
          <ProtectedRoute>
            <PortalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PortalDashboard />} />
        <Route path="tickets" element={<PortalTicketsPage />} />
        <Route path="tickets/nuevo" element={<PortalNuevoTicketPage />} />
        <Route path="tickets/:id" element={<PortalTicketDetallePage />} />
        <Route path="conversaciones" element={<PortalConversacionesPage />} />
        <Route path="conversaciones/nueva" element={<PortalNuevaConversacionPage />} />
        <Route path="conversaciones/:id" element={<PortalConversacionPage />} />
        <Route path="conocimiento" element={<PortalConocimientoPage />} />
        <Route path="conocimiento/:id" element={<PortalArticuloPage />} />
        <Route path="perfil" element={<PortalPerfilPage />} />
      </Route>

      {/* CSAT Encuesta pública (sin auth) */}
      <Route path="/csat/:token" element={<EncuestaPage />} />

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <ToastProvider />
    </TooltipProvider>
  );
}

export default App;
